/**
 * @namespace Utils
 * @class GoogleAnalytics
 * @exports Utils/GoogleAnalytics
 * @description Util for working with Google Analytics
 * @date 2020/02/21
 * @license no license
 * @copywrite Answers In Retirement Limited
 */
export default class GoogleAnalytics {
	static sendEvent(event, obj) {
		// Google Analytics event
		obj.event = event;
		window.dataLayer.push(obj);
	}

	/**
	 * @async @name borrowingForecastOpened
	 * @param data parameter data
	 * @param data.gtm.element.dataset.gaId The cta location
	 * @param data.gtm.element.dataset.gaValue The type of calculator
	 */
	static borrowingForecastOpened(data = {}) {
		this.sendEvent('borrowingForecastOpened', data);
	}

	/**
	 * @async @name productToggled
	 * @param data parameter data
	 * @param data.provider The name of the provider
	 * @param data.product_name The name of the product
	 */
	static productToggled(data = {}) {
		this.sendEvent('product_toggled', data);
	}

	/**
	 * @async @name moreActionSelected
	 * @param data parameter data
	 * @param data.selected_action The name of the action
	 */
	static moreActionSelected(data = {}) {
		this.sendEvent('more_action_selected', data);
	}

	/**
	 * @async @name viewCart
	 * @param data parameter data
	 * @param data.ecommerce Object
	 * @param data.ecommerce.items Array
	 * @param data.ecommerce.items[].item_id Product UUID
	 * @param data.ecommerce.items[].index Array index
	 * @param data.ecommerce.items[].item_name Product name
	 * @param data.ecommerce.items[].item_brand Provider name
	 * @param data.gtm.element.dataset.gaId The cta location
	 */
	static viewCart(data = {}) {
		this.sendEvent('viewCart', data);
	}

	/**
	 * @async @name kfiRequested
	 * @param data parameter data
	 * @param data.provider The name of the provider(s)
	 * @param data.product_name The name of the product(s)
	 * @param data.no_of_products The number of products
	 */
	static kfiRequested(data = {}) {
		this.sendEvent('kfi_requested', data);
	}

	/**
	 * @async @name sourcingSessionStarted
	 * @param data parameter data
	 */
	static sourcingSessionStarted(data = {}) {
		this.sendEvent('sourcing_session_started', data);
	}

	/**
	 * @async @name applicantAdded
	 * @param data parameter data
	 * @param data.action The action for this event, e.g. 'added'
	 */
	static applicantAdded(data = {}) {
		this.sendEvent('applicant_added', data);
	}

	/**
	 * @async @name healthStatusUpdated
	 * @param data parameter data
	 * @param data.action The action for this event, e.g. 'updated'
	 */
	static healthStatusUpdated(data = {}) {
		this.sendEvent('health_status_updated', data);
	}

	/**
	 * @async @name healthStatusRemoved
	 * @param data parameter data
	 * @param data.action The action for this event, e.g. 'removed'
	 */
	static healthStatusRemoved(data = {}) {
		this.sendEvent('health_status_removed', data);
	}

	/**
	 * @async @name propertyDetailsAdded
	 * @param data parameter data
	 * @param data.action The action for this event, e.g. 'added'
	 */
	static propertyDetailsAdded(data = {}) {
		this.sendEvent('property_details_added', data);
	}

	/**
	 * @async @name floodReportChecked
	 * @param data parameter data
	 * @param data.option The action for this event, e.g. 'Flood Risk for Planning Report'
	 */
	static floodReportChecked(data = {}) {
		this.sendEvent('flood_report_checked', data);
	}

	/**
	 * @async @name borrowingUpdated
	 * @param data parameter data
	 * @param data.show_max_available Show Maximum Available
	 * @param data.initial_advance The amount the user has added
	 * @param data.add_arrangement_fee If the product allows, do you wish to add the Arrangement fee to the advance?
	 * @param data.drawdown_required Drawdown facility required
	 * @param data.use_age_next_birthday Use age next birthday for calculations
	 * @param data.protected_equity_required Protected equity required
	 * @param data.erc_approved_only ERC approved only products shown
	 */
	static borrowingUpdated(data = {}) {
		this.sendEvent('borrowing_updated', data);
	}

	/**
	 * @async @name productsSelected
	 * @param data parameter data
	 * @param data.num_products The number of products selected e.g 1, 2, 3 or 4
	 * @param data.lifetime_voluntary_payment Lifetime mortgages - voluntary payment selected or not
	 * @param data.lifetime_interest_serviced Lifetime mortgages - interest serviced selected or not
	 * @param data.lifetime_mandatory_payments Lifetime mortgages - mandatory payment selected or not
	 * @param data.retirement_mortgage Retirement mortgages - selected or not
	 * @param data.monthly_repayment_amount Monthly repayment amount
	 * @param data.repayment_term_years Repayment term in years e.g full_term, 5_years, 10_years
	 * @param data.maximise_affordability Maximise affordability selected or not
	 */
	static productsSelected(data = {}) {
		this.sendEvent('products_selected', data);
	}

	/**
	 * @async @name knowledgeBankCriteriaSubmitted
	 * @param data parameter data
	 * @param data[gtm.element.dataset.gaId] The location of the feature
	 * @param data[gtm.element.dataset.gaValue] String of selected criteria
	 * @param data.criteria_selected_count The number of crtieria items selected
	 */
	static knowledgeBankCriteriaSubmitted(data = {}) {
		this.sendEvent('knowledgeBankCriteriaSubmitted', data);
	}

    /**
	 * @async @name navClick
	 * @param data parameter data
	 * @param data.link_text The text of the link clicked
	 * @param data.link_url The url of the link clicked
	 */
	static navClick(data = {}) {
		this.sendEvent('nav_click', data);
	}

	/**
	 * @async @name sideNavClick
	 * @param data parameter data
	 * @param data.link_text The text of the link clicked
	 * @param data.link_url The url of the link clicked
	 */
	static sideNavClick(data = {}) {
		this.sendEvent('side_nav_click', data);
	}

	/**
	 * @async @name formView
	 * @param data parameter data
	 * @param data.form_name The name of the form viewed
	 * @param data.form_step_name The name of the form step viewed
	 * @param data.form_step The number of the form step viewed
	 */
	static formView(data = {}) {
		this.sendEvent('form_view', data);
	}

	/**
	 * @async @name formInitiate
	 * @param data parameter data
	 * @param data.form_name The name of the form initiated
	 * @param data.form_step_name The name of the form step initiated
	 * @param data.form_step The number of the form step initiated
	 */
	static formInitiate(data = {}) {
		this.sendEvent('form_initiate', data);
	}

	/**
	 * @async @name formComplete
	 * @param data parameter data
	 * @param data.form_name The name of the form completed
	 */
	static formComplete(data = {}) {
		this.sendEvent('form_complete', data);
	}

    /**
	 * @async @name maxLtvOpened
	 * @param data parameter data
	 */
	static maxLtvOpened(data = {}) {
		this.sendEvent('maxLtvOpened', data);
	}

    /**
	 * @async @name maxLtvError
	 * @param data parameter data
	 */
	static maxLtvError(data = {}) {
		this.sendEvent('maxLtvError', data);
	}

    /**
	 * @async @name nearMissesOpened
	 * @param data parameter data
	 */
	static nearMissesOpened(data = {}) {
		this.sendEvent('nearMissesOpened', data);
	}

    /**
	 * @async @name nearMissSurveyOpened
	 * @param data parameter data
	 */
	static nearMissSurveyOpened(data = {}) {
		this.sendEvent('nearMissSurveyOpened', data);
	}

    /**
	 * @async @name nearMissesSubmitted
	 * @param data parameter data
	 */
	static nearMissesSubmitted(data = {}) {
		this.sendEvent('nearMissesSubmitted', data);
	}
}
